import { ThunkDispatch } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { AnyAction, Middleware } from "redux";
import { getIdToken } from "../actions/login.action";
import {
  clearSessionExpiry,
  requestSessionExtension,
  resolveSessionExtension,
  showSessionExpiredConsent,
  showSessionHardCapExpired,
} from "../actions/session.action";
import { baseUrl } from "../config";
import { QueryActions } from "../models";
import { RootState } from "../store";
export class ForbiddenError extends Error {
  readonly status = 403;
  constructor() {
    super("Not authorized");
    this.name = "ForbiddenError";
  }
}

export const idTokenName = "__lmsToken";
export let token: string = "";

export interface MiddlewareAction {
  actionName: string;
  body?: object;
  type?: string;
  url?: string;
  authType?: string;
  token?: string;
  queryParams?: Record<string, number | string | string[] | undefined>;
  metadata?: Record<string, any>;
  throwOnError?: boolean;
}

export function saveToken(idToken: string) {
  token = idToken;
  window.localStorage.setItem(idTokenName, idToken);
}
export function getToken() {
  return window.localStorage.getItem(idTokenName);
}

function getHeader(base64Str: string, authType?: string) {
  return {
    "Content-Type": "application/json",
    Authorization: `${authType ? authType : "Bearer"} ${base64Str}`,
  };
}
export function turnOnDnd(localStorageName: string, value: string) {
  window.localStorage.setItem(localStorageName, value);
}
export function isDndOn(localStorageName: string, value: string) {
  return window.localStorage.getItem(localStorageName) === value;
}
export async function getImage(doc_type: string, data: object, url: string) {
  const response = await axios.post(baseUrl + url, data, {
    headers: {
      Authorization: "Bearer " + token,
    },
    responseType: "blob",
  });
  return Promise.resolve(response);
}
function getBackendErrorMessage(error: any): string {
  const data = error?.response?.data ?? error?.data;
  if (!data) return error?.message || "Something went wrong";
  if (typeof data === "string") return data;
  const message = data?.message;
  if (typeof message === "string") return message;
  if (message && typeof message === "object") {
    const nested = message?.description || message?.title;
    if (typeof nested === "string") return nested;
  }
  if (typeof data?.error === "string") return data.error;
  return error?.message || "Something went wrong";
}

export default async function dispatch(
  dispatch: any,
  actionDetails: MiddlewareAction,
) {
  let splitActionName = actionDetails.actionName.split("_");
  dispatch({
    type: actionDetails.actionName,
    payload: actionDetails.body,
    metadata: actionDetails.metadata,
  });
  if (splitActionName[0] === "REQUEST") {
    let axiosConfig: any;
    try {
      const idToken = actionDetails.token
        ? actionDetails.token
        : await getIdToken();
      let response: any = undefined;
      axiosConfig = {
        headers: getHeader(idToken, actionDetails.authType),
        timeout: 30000,
        params: actionDetails.queryParams,
      };
      switch (actionDetails.type) {
        case "GET":
          response = await axios.get(
            `${baseUrl}${actionDetails.url}`,
            axiosConfig,
          );
          break;
        case "POST":
          response = await axios.post(
            `${baseUrl}${actionDetails.url}`,
            actionDetails.body,
            axiosConfig,
          );
          break;
        case "PATCH":
          response = await axios.patch(
            `${baseUrl}${actionDetails.url}`,
            actionDetails.body,
            axiosConfig,
          );
          break;
        case "DELETE":
          response = await axios.delete(
            `${baseUrl}${actionDetails.url}`,
            axiosConfig,
          );
          break;
      }

      if (
        response &&
        (response.status === 200 ||
          response.status === 201 ||
          response.status === 204)
      ) {
        dispatch({
          type: `${actionDetails.actionName}_SUCCESS`,
          payload: response.data,
          metadata: actionDetails.metadata,
        });
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      const errorStatus = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      const errorData = axios.isAxiosError(error)
        ? error.response?.data
        : undefined;
      const errorCode =
        errorData && typeof errorData === "object" && "code" in errorData && typeof errorData.code === "string"
          ? errorData.code
          : undefined;

      if (errorStatus === 401 && errorCode === "TOKEN_EXPIRED_BY_AGE") {
        // 15-min idle expiry — show the consent screen and wait for the user.
        // No auto-refresh: the request is retried only after explicit consent.
        dispatch(showSessionExpiredConsent());
        const consented = await requestSessionExtension();
        dispatch(clearSessionExpiry());
        if (consented) {
          try {
            const freshToken = await getIdToken(true);
            const retryConfig = {
              ...axiosConfig,
              headers: getHeader(freshToken, actionDetails.authType),
            };
            let retryResponse: AxiosResponse | undefined;
            switch (actionDetails.type) {
              case 'GET':
                retryResponse = await axios.get(`${baseUrl}${actionDetails.url}`, retryConfig);
                break;
              case 'POST':
                retryResponse = await axios.post(
                  `${baseUrl}${actionDetails.url}`, actionDetails.body, retryConfig,
                );
                break;
              case 'PATCH':
                retryResponse = await axios.patch(
                  `${baseUrl}${actionDetails.url}`, actionDetails.body, retryConfig,
                );
                break;
              case 'DELETE':
                retryResponse = await axios.delete(
                  `${baseUrl}${actionDetails.url}`, retryConfig,
                );
                break;
            }
            if (
              retryResponse &&
              (retryResponse.status === 200 || retryResponse.status === 201 || retryResponse.status === 204)
            ) {
              dispatch({
                type: `${actionDetails.actionName}_SUCCESS`,
                payload: retryResponse.data,
                metadata: actionDetails.metadata,
              });
              return retryResponse.data;
            } else if (retryResponse) {
              // Retry succeeded with a status outside the SUCCESS whitelist
              // (e.g. 202) — mirror the original path and return the response
              // instead of reporting FAILURE.
              return retryResponse;
            }
          } catch {
            // Retry also failed — fall through to original failure dispatch
          }
        }
      } else if (errorStatus === 401 && errorCode === "SESSION_HARD_CAP_EXPIRED") {
        // 2-hour absolute cap — forced logout. No consent, no refresh.
        // Settle any pending consent gate so a concurrent consent request does
        // not hang after the hard-cap screen unmounts the consent modal. Return
        // early to skip the per-request FAILURE dispatch — the app is replacing
        // itself with the hard-cap screen.
        resolveSessionExtension(false);
        dispatch(showSessionHardCapExpired());
        return;
      }
      // Original failure dispatch
      console.log(error);
      dispatch({
        type: `${actionDetails.actionName}_FAILURE`,
        payload: errorData,
        metadata: actionDetails.metadata,
      });
      if (errorStatus === 403 && actionDetails.throwOnError) {
        throw new ForbiddenError();
      }
      if (actionDetails.throwOnError) {
        throw new Error(getBackendErrorMessage(error));
      }
    }
  }
}

export async function getAuthorizedHeaders(
  dispatch: ThunkDispatch<RootState, void, AnyAction>,
  actionDetails: QueryActions,
) {
  try {
    let authToken = actionDetails?.token;
    if (!authToken) authToken = (await getIdToken()) ?? null;
    return getHeader(authToken!, actionDetails?.authType);
  } catch (error: any) {
    let errorResponse: any = {};
    if (error?.data) errorResponse = error.data;
    else if (error?.response?.data) errorResponse = error.response.data;
    dispatch({
      type: "UNAUTHORIZED",
      payload: {
        unauthorized: {
          status: true,
          errorCode: errorResponse?.errorCode || "UNKNOWN_ERROR",
          title: errorResponse?.message?.title || "Unauthorized",
          message: errorResponse?.message?.description || "Unauthorized access",
        },
      },
    });
  }
}

export function combineActionMiddlewares(...middlewares: Middleware[]) {
  return middlewares.flat();
}
